import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AppService } from 'src/app/app.service';
import { AuthService } from 'src/app/_core/auth.service';
import { OrderService } from 'src/app/_services/order.service';

import { get, assignIn, each } from 'lodash';

@Injectable({
	providedIn: 'root'
})
export class GiftcardService {

	constructor(
		private httpClient: HttpClient,
		public appService: AppService,
		public authService: AuthService,
		public orderService: OrderService,
	) { }

	init(accounts: any) {
		const storage = this.orderService.$storage;
		storage.giftCards = {
			cards: [],
			accounts,
			roundTo: (() => {
				const denomination = get(storage, 'rosConfig.currencySettings.minimalDiscountDenomination', 1);
				switch (denomination) {
					case 1: return 2;
					case 10: return 1;
					case 100: return 0;
					default: return 2;
				}
			})()
		}
	}

	clear() {
		const $storage = this.orderService.$storage;
		if ($storage.giftCards) {
			assignIn($storage.giftCards, {
				cards: []
			});
			this.orderService.calculateBasketTotal();
			this.orderService.cacheOrder();
		}
	}

	checkGiftCard(giftCard: any) {
		const $storage = this.orderService.$storage;
		const payload = {
			cardNumber: giftCard.pan,
			cvv: giftCard.cvv,
			pincode: giftCard.otp,
			orderId: $storage.orderId,
		};
		return this.post('/services/loyalty/pos/get-money-balance', payload , giftCard.$$loyaltyApiKey)
			.then(res => {
				if (res.IsSuccess) {
					const balance = get(res, 'ResponseData.balance', 0);
					if (!balance) throw ({ Key: 'balance' });
					return { balance: balance };
				} else {
					throw ({
						message: res.Message
					});
				}
			}).catch(err => {
				if (err?.error) err = err.error;
				throw (err);
			})
	}

	addGiftCard(pm: any) {
		const $storage = this.orderService.$storage;
		if (!$storage.giftCards?.cards) $storage.giftCards.cards = [];
		$storage.giftCards.cards.push(pm);
		this.sanitizeGiftCards();
	}

	deleteGiftCard(pm: any) {
		const $storage = this.orderService.$storage;
		const index = $storage.giftCards?.cards.findIndex(card => card.account === pm.ccinfo.accountId);

		if (index !== -1) {
			$storage.giftCards.cards.splice(index, 1);
			this.sanitizeGiftCards();
		}
	}

	sanitizeGiftCards() {
		let $storage = this.orderService.$storage;
		let total = 0;
		each($storage.giftCards.cards, card => {
			total += card.amount;
		});
		this.orderService.calculateBasketTotal();
		this.orderService.cacheOrder();
	}

	getPrePaidBalance() {
		const $storage = this.orderService.$storage;

		if (this.clubHasGiftCardPayment() && $storage.loyaltyData?.balance?.prePaid) {
			const prePaidGiftCard = $storage.giftCards?.cards?.find(card => [card.paymentType, card.paymentMethod].includes('giftCard'));
			return prePaidGiftCard ? ($storage.loyaltyData?.balance?.prePaid - prePaidGiftCard.amount) : $storage.loyaltyData?.balance?.prePaid;
		}
		return 0;
	}

	isPrePaidUsed() {
		let $storage = this.orderService.$storage;
		return !!$storage.giftCards?.cards?.find(card => [card.paymentType, card.paymentMethod].includes('giftCard'));
	}

	clubHasGiftCardPayment() {
		const $storage = this.orderService.$storage;
		if (!$storage.loyaltyClub) return false;
		return !!$storage?.config?.paymentAccounts?.find(payment => this.paymentValidForClubAndService(payment));
	}

	paymentValidForClubAndService(payment: any) {
		const $storage = this.orderService.$storage;
		return [payment.paymentType, payment.paymentMethod].includes('giftCard') && ([payment.services].includes($storage.orderMode) || !payment?.services?.length) && payment.loyaltyApiKey.toLowerCase() == $storage.loyaltyClub.accountGuid.toLowerCase();
	}

	// ----------------------------------------------------------------------------------------------------------->
	// API
	// ----------------------------------------------------------------------------------------------------------->

	getLoyaltyHeaders(accountGuid: any) {
		const appConfig = this.appService.appConfig;
		const $storage = this.orderService.$storage;
		return {
			headers: new HttpHeaders({
				env: appConfig.loyaltyEnv,
				'Content-Type': 'application/json',
				'Authorization': this.authService.appHttpOptions.headers.get('Authorization'),
				'JoinChannelGuid': appConfig.loyaltyJoinChannelGUID,
				'siteId': $storage.siteId,
				accountGuid
			})
		};
	}

	public post(url: any, payload: any, accountGuid: any): Promise<any> {
		return new Promise((resolve, reject) => {
			const tabitAPI = this.appService.appConfig.tabitOrder_tabitAPI || this.appService.appConfig.tabitAPI;
			this.httpClient.post(tabitAPI + url, payload || {}, this.getLoyaltyHeaders(accountGuid))
				.subscribe({
					next: (results: any) => resolve(results),
					error: (e) => reject(e)
				});
		});
	}

}
